import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import AiBadge from "./AiBadge";
import LanguageToggle from "./LanguageToggle";
import { useI18n } from "../i18n/LanguageContext";
import { scrollToSection, SECTION_IDS } from "../lib/scrollNav";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("start");
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const NAV = t.nav;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scrollspy: aktiven Abschnitt anhand der Scrollposition bestimmen (nur auf "/").
  useEffect(() => {
    if (location.pathname !== "/") return;
    const onScroll = () => {
      const offset = 120;
      let current = SECTION_IDS[0];
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= offset) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const go = useCallback(
    (id) => {
      setOpen(false);
      if (location.pathname !== "/") {
        navigate(`/#${id}`);
      } else {
        scrollToSection(id);
      }
    },
    [location.pathname, navigate]
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur shadow-sm" : "bg-white"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 flex items-center justify-between h-[76px]">
        <button onClick={() => go("start")} data-testid="logo-link" aria-label="InaTeB" className="cursor-pointer">
          <Logo height={52} />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => {
            const isActive = location.pathname === "/" && active === item.id;
            return (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                data-testid={`nav-${item.id}`}
                className={`nav-underline text-[15px] font-semibold uppercase tracking-wide transition-colors cursor-pointer ${
                  isActive ? "active text-blue-brand" : "text-ink hover:text-blue-brand"
                }`}
              >
                {item.label}
              </button>
            );
          })}
          <LanguageToggle className="ml-1" />
        </nav>

        <button
          className="lg:hidden text-ink"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {open && (
        <nav className="relative lg:hidden bg-white border-t border-line px-5 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          <AiBadge className="absolute top-2 right-3" />
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              data-testid={`mobile-nav-${item.id}`}
              className={`text-left py-3 px-3 rounded-md text-base font-semibold uppercase tracking-wide ${
                location.pathname === "/" && active === item.id ? "bg-secondary text-blue-brand" : "text-ink"
              }`}
            >
              {item.label}
            </button>
          ))}
          <div className="pt-3 mt-2 border-t border-line">
            <LanguageToggle />
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
